/* ---------- small helpers ---------- */
export const fmt = (n) => Number(n || 0).toLocaleString('en-US');
export const pct = (n, d) => (d ? `${((100 * n) / d).toFixed(1)}%` : '—');
export const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c]);
export const age = (m) => {
    const v = Number(m) || 0;
    if (v < 60) return `${v}m`;
    if (v < 1440) return `${(v / 60).toFixed(v < 600 ? 1 : 0)}h`;
    return `${(v / 1440).toFixed(1)}d`;
};

export const RAMP = ['#0E7C6B', '#3E9A84', '#7BAE7C', '#B2B96A', '#DFA845', '#D97A38', '#C4502A', '#96261C'];
export const TREND_C = ['#96261C', '#D97A38', '#0E7C6B', '#2E5A8F', '#B2903A', '#7A3D8F', '#3E9A84', '#C4502A'];

// Facility code -> id, for the on-demand location drill-down.
export const FACID = { BWI1: 9, CHI1: 10, SAT1: 13, TRA1: 17, LAX1: 11, PHL1: 3, DFW1: 7, SLC1: 4, PDX1: 12 };

/* ---------- item categories: what actually breaks a recipe (§4.6) ---------- */
const PROTEIN =
    /chicken|beef|pork|salmon|shrimp|steak|turkey|sausage|\bcod\b|tilapia|lamb|bacon|scallop|\btofu\b|meatball|chorizo|prosciutto|\bham\b|\bduck\b|halibut|tuna|tenderloin|brisket|patt(y|ies)/i;
const PANTRY =
    /spice|seasoning|flake|powder|dried|\bblend\b|sauce|glaze|dressing|vinegar|\boil\b|sugar|honey|syrup|paste|stock|broth|panko|breadcrumb|caper|\bsalt\b|marinade|mayo|mustard|ketchup|jam|extract/i;
const BASE =
    /\brice\b|pasta|noodle|gnocchi|tortilla|\bbun\b|potato|couscous|orzo|ramen|udon|yakisoba|spaghetti|penne|linguine|farro|quinoa|barley|dumpling|flatbread|\bnaan\b|\bpita\b|\broll\b|polenta|grits/i;
const FRESH =
    /cheese|cream|\begg|butter|milk|yogurt|mozzarella|parmesan|feta|cheddar|lettuce|spinach|arugula|cucumber|tomato|pepper|onion|scallion|garlic|lime|lemon|cilantro|mushroom|carrot|zucchini|broccoli|cabbage|kale|avocado|basil|parsley|slaw|greens|squash|corn|bean/i;
export const CATS = {
    protein: { w: 3, lab: 'PROTEIN', col: '#96261C' },
    base: { w: 2, lab: 'BASE', col: '#C4502A' },
    fresh: { w: 1.3, lab: 'FRESH', col: '#DFA845' },
    pantry: { w: 1, lab: 'PANTRY', col: '#5B7183' },
};
export function categorise(n) {
    if (PROTEIN.test(n)) return 'protein';
    if (PANTRY.test(n)) return 'pantry';
    if (BASE.test(n)) return 'base';
    if (FRESH.test(n)) return 'fresh';
    return 'pantry';
}

/* ---------- ISO week helpers ---------- */
export function isoWeekYear(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = (d.getUTCDay() + 6) % 7; // Mon = 0
    d.setUTCDate(d.getUTCDate() - dayNum + 3); // nearest Thursday
    const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
    firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
    const week = 1 + Math.round((d - firstThursday) / (7 * 24 * 3600 * 1000));
    return { week, year: d.getUTCFullYear() };
}

/**
 * Calendar date used as the BAMS pack-week anchor. On Fri–Sun this is the next
 * Monday so the page already targets the upcoming ISO pack week.
 *
 * @param {Date} [date]
 * @returns {Date}
 */
export function bamsPackWeekAnchor(date = new Date()) {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay(); // 0=Sun … 5=Fri, 6=Sat
    if (day === 5 || day === 6 || day === 0) {
        d.setDate(d.getDate() + (day === 0 ? 1 : 8 - day));
    }
    return d;
}

/**
 * Current + prior pack weeks for the feed. Prior is one calendar week before the
 * anchor (setDate −7), not −168h, so DST spring-forward cannot skip a week.
 *
 * @param {Date} [date]
 * @returns {{cur: {week: number, year: number}, prv: {week: number, year: number}}}
 */
export function bamsPackWeekPair(date = new Date()) {
    const anchor = bamsPackWeekAnchor(date);
    const prior = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate());
    prior.setDate(prior.getDate() - 7);
    return { cur: isoWeekYear(anchor), prv: isoWeekYear(prior) };
}

/* ---------- normalise API payloads to the prototype's internal shape ---------- */
export const mapFeedRow = (r) => ({
    brand: r.brand,
    facility: r.facility,
    year: Number(r.year) || 0,
    week: Number(r.week) || 0,
    pd: String(r.packDate || '').slice(0, 10),
    packed: Number(r.ordersPacked) || 0,
    short_ord: Number(r.ordersWithShort) || 0,
    lines: Number(r.lineShorts) || 0,
    o0: Number(r.orders0) || 0,
    o1: Number(r.orders1) || 0,
    o2: Number(r.orders2) || 0,
    o3: Number(r.orders3Plus) || 0,
    swapcards: Number(r.swapCards) || 0,
});
export const mapSkuRow = (r) => ({
    brand: r.brand,
    facility: r.facility,
    facilityId: Number(r.facilityId) || null,
    week: Number(r.week) || 0,
    sku: String(r.externalSku || ''),
    name: String(r.itemName || ''),
    orders: Number(r.ordersAffected) || 0,
    days: Number(r.daysShort) || 0,
    last: String(r.lastShortUtc || ''),
    cat: categorise(String(r.itemName || '')),
});

/* ---------- rollups ---------- */
export function roll(rows, filterBrands) {
    const m = new Map();
    rows.forEach((r) => {
        if (filterBrands && filterBrands.size && !filterBrands.has(r.brand)) return;
        const k = `${r.brand}|${r.facility}`;
        const e = m.get(k) || {
            brand: r.brand,
            facility: r.facility,
            packed: 0,
            short_ord: 0,
            lines: 0,
            o0: 0,
            o1: 0,
            o2: 0,
            o3: 0,
            swapcards: 0,
        };
        ['packed', 'short_ord', 'lines', 'o0', 'o1', 'o2', 'o3', 'swapcards'].forEach((f) => {
            e[f] += r[f];
        });
        m.set(k, e);
    });
    return [...m.values()].sort((a, b) => b.lines / Math.max(b.packed, 1) - a.lines / Math.max(a.packed, 1));
}
export const perPacked = (e) => (e.packed ? e.lines / e.packed : 0);
export const perAff = (e) => (e.short_ord ? e.lines / e.short_ord : 0);
export function stateOf(v) {
    if (v >= 5) return ['CRITICAL', RAMP[7]];
    if (v >= 2) return ['ELEVATED', RAMP[5]];
    if (v >= 0.75) return ['WATCH', RAMP[4]];
    return ['STEADY', RAMP[0]];
}

// Grid cell shade = share of the day's short orders (ported from prototype).
export function gridShade(v, isDark) {
    if (v <= 0) return 'var(--tint)';
    const i = Math.min(7, Math.floor(v * 14));
    const light = ['#F2F8F6', '#E4F0EC', '#D6E7DF', '#EFEAD2', '#F7E3C0', '#F6D0AC', '#EFB79A', '#E39C8B'];
    const dark = ['#16302B', '#194037', '#1D4F42', '#3A3B22', '#4A3A1E', '#573A22', '#5E3628', '#6B3129'];
    return (isDark ? dark : light)[i];
}

/* ---------- CSV + clipboard ---------- */
export function downloadText(text, filename, mime = 'text/csv;charset=utf-8;') {
    const blob = new Blob([text], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
export function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
    }
}
